import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";

const CustomerAuthContext = createContext(null);
const LS_KEY = "xd_customer_token";

const client = axios.create({ baseURL: API_BASE });

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const withAuth = (token) => ({ headers: { Authorization: `Bearer ${token || localStorage.getItem(LS_KEY)}` } });

  useEffect(() => {
    const token = localStorage.getItem(LS_KEY);
    if (!token) { setLoading(false); return; }
    client.get("/customer/me", withAuth(token))
      .then((r) => setCustomer(r.data))
      .catch(() => localStorage.removeItem(LS_KEY))
      .finally(() => setLoading(false));
  }, []);

  const register = async (payload) => {
    const res = await client.post("/customer/register", payload);
    localStorage.setItem(LS_KEY, res.data.token);
    setCustomer(res.data.customer);
    return res.data.customer;
  };

  const login = async (email, password) => {
    const res = await client.post("/customer/login", { email, password });
    localStorage.setItem(LS_KEY, res.data.token);
    setCustomer(res.data.customer);
    return res.data.customer;
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setCustomer(null);
  };

  const updateProfile = async (payload) => {
    const res = await client.put("/customer/me", payload, withAuth());
    setCustomer(res.data);
    return res.data;
  };

  const changePassword = async (payload) => {
    await client.post("/customer/change-password", payload, withAuth());
  };

  const fetchMyOrders = async () => {
    const res = await client.get("/customer/orders", withAuth());
    return res.data;
  };

  const fetchWishlist = async () => {
    const res = await client.get("/customer/wishlist", withAuth());
    // sync IDs onto local customer state so hearts render instantly
    setCustomer((c) => (c ? { ...c, wishlist: res.data.map((p) => p.id) } : c));
    return res.data;
  };

  const addToWishlist = async (productId) => {
    await client.post(`/customer/wishlist/${productId}`, {}, withAuth());
    setCustomer((c) => (c ? { ...c, wishlist: Array.from(new Set([...(c.wishlist || []), productId])) } : c));
  };

  const removeFromWishlist = async (productId) => {
    await client.delete(`/customer/wishlist/${productId}`, withAuth());
    setCustomer((c) => (c ? { ...c, wishlist: (c.wishlist || []).filter((id) => id !== productId) } : c));
  };

  const isInWishlist = (productId) => !!customer?.wishlist?.includes(productId);

  return (
    <CustomerAuthContext.Provider value={{ customer, loading, register, login, logout, updateProfile, changePassword, fetchMyOrders, fetchWishlist, addToWishlist, removeFromWishlist, isInWishlist, getToken: () => localStorage.getItem(LS_KEY) }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() { return useContext(CustomerAuthContext); }
